/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useRef} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {Text, Box, Button, useMultiStyleConfig, Flex} from '@chakra-ui/react'
import {
    ADD_FILTER,
    REMOVE_FILTER,
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {remCalc} from '../../../theme/_utils'
import DynamicIcon from '../../../components/dynamic-icon/DynamicIcon'

const RefinementsTab = ({toggleFilter, selectedFilterValues, filters}) => {
    const sanitizedFilters = filters?.filter((f) => f.attributeId !== 'c_isPDPOnlyPurchasable')
    const styles = useMultiStyleConfig('Filters', {variant: 'selectedFilterButton'})
    const {formatMessage} = useIntl()

    /**
     * Freeze the first valid sanitizedFilters array synchronously during render.
     *
     * The Search API omits refinement values with zero results, so after a filter
     * is applied some "preferred" filter values simply disappear from the response.
     * By freezing the initial set we keep those values in the DOM and show them
     * with a hit count of 0 instead of hiding them entirely.
     *
     * Done inline (not in useEffect) to avoid a one-render delay and to survive
     * potential component remounts without losing the frozen snapshot.
     */
    const frozenFilters = useRef(null)
    if (sanitizedFilters && sanitizedFilters.length > 0 && !frozenFilters.current) {
        frozenFilters.current = sanitizedFilters
    }

    // Use the frozen snapshot as the structural source of truth.
    // Fall back to the live prop only before the first valid response arrives.
    const activeFilters = frozenFilters.current || sanitizedFilters || []

    /**
     * Build a two-level lookup map from the CURRENT (live) sanitizedFilters:
     *   { [attributeId]: { [value]: hitCount } }
     *
     * This lets us efficiently retrieve the up-to-date hit count for any
     * filter value while still iterating over the frozen structure above.
     * Values absent from the current response will simply not be in the map,
     * so the lookup falls back to 0.
     */
    const currentHitCountMap = {}
    if (sanitizedFilters) {
        for (const filter of sanitizedFilters) {
            currentHitCountMap[filter.attributeId] = {}
            for (const val of filter.values || []) {
                currentHitCountMap[filter.attributeId][val.value] = val.hitCount ?? 0
            }
        }
    }

    // ─── Style helpers ────────────────────────────────────────────────────────

    const getFilterStyleState = (filter) => {
        const hasHits = Number(filter?.hitCount ?? 0) > 0
        const isSelected = Boolean(filter?.isSelected)
        return {hasHits, isSelected}
    }

    const getFilterButtonStyleProps = (filter) => {
        const {hasHits, isSelected} = getFilterStyleState(filter)

        return {
            _hover: isSelected
                ? {opacity: 0.8}
                : {
                      bg: 'background.neutral.hover',
                      boxShadow:
                          'inset 0 0 0 1px var(--border-neutral-highlight, #FFF), 0 1px 2px 0 var(--neutral-xs, rgba(19, 24, 58, 0.08)), 0 0 0 1px var(--neutral-xs, rgba(19, 24, 58, 0.08))',
                  },
            _focusVisible: {
                boxShadow:
                    '0 0 0 1px var(--chakra-colors-background-base-default, #EFEDEA) inset, 0 1px 2px 0 var(--chakra-shadows-neutral-xs, rgba(19, 24, 58, 0.08)), 0 0 0 2px var(--chakra-colors-border-neutral-focus, #6C7BA8)',
            },
            boxShadow: isSelected
                ? 'inset 0 0 0 1px var(--border-neutral-highlight, #FFF), 0 1px 2px 0 var(--neutral-xs, rgba(19, 24, 58, 0.08)), 0 0 0 1px var(--neutral-xs, rgba(19, 24, 58, 0.08))'
                : 'none',
            bg: hasHits
                ? isSelected
                    ? 'background.neutral.dark'
                    : 'background.base.under'
                : 'background.base.under',
            opacity: hasHits ? 'unset' : 0.9,
            color: hasHits
                ? isSelected
                    ? 'foreground.neutral.inverse'
                    : 'foreground.neutral.default'
                : 'foreground.neutral.dim',
        }
    }

    const getFilterIconColor = (filter) => {
        const {hasHits, isSelected} = getFilterStyleState(filter)
        if (!hasHits) return 'foreground.neutral.default'
        return isSelected ? 'foreground.neutral.inverse' : 'foreground.neutral.default'
    }

    const getRightIconColor = (filter) => {
        const {isSelected} = getFilterStyleState(filter)
        return isSelected ? 'foreground.neutral.dim' : 'foreground.neutral.subtle'
    }

    // ─── Build preferred filters from the frozen structure ────────────────────

    const preferredFilters = []

    for (const key in activeFilters) {
        for (const value in activeFilters[key].values) {
            if (activeFilters[key].values[value].description?.includes('preferred')) {
                const attributeId = activeFilters[key].attributeId
                const filterValue = activeFilters[key].values[value].value
                const description = activeFilters[key].values[value].description

                const damLinkMatch = description?.match(/https:\/\/dam\.delonghi\.com\/[^\s"')<>]+/)
                const iconUrl = damLinkMatch ? damLinkMatch[0] : null

                const isSelected = selectedFilterValues?.[attributeId]
                    ?.split('|')
                    .includes(filterValue)

                /**
                 * Hit count is always read from the live lookup map so it
                 * reflects the current search result. If the value is absent
                 * from the current response (API omitted it because it has no
                 * matches) the lookup returns undefined and we default to 0.
                 */
                const hitCount = currentHitCountMap[attributeId]?.[filterValue] ?? 0

                preferredFilters.push({
                    ...activeFilters[key].values[value],
                    attributeId,
                    iconUrl,
                    hitCount,
                    isSelected,
                })
            }
        }
    }

    const displayPreferredFilters = preferredFilters.map((filter) => ({
        hitCount: Number(filter.hitCount ?? 0),
        key: `preferred-${filter.attributeId}-${filter.value}`,
        label: filter.label,
        iconId: filter.attributeId?.replace(/^c_/, ''),
        isSelected: Boolean(filter.isSelected),
        onClick: () =>
            toggleFilter(
                {value: filter.value},
                filter.attributeId,
                '',
                Boolean(filter.isSelected),
                true,
                'quick',
            ),
        ariaLabel: formatMessage(filter.isSelected ? REMOVE_FILTER : ADD_FILTER, {
            label: filter.label,
        }),
        iconUrl: filter.iconUrl,
    }))

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            {displayPreferredFilters.length > 0 && (
                <Box
                    data-testid="sf-selected-refinements"
                    {...styles?.selectedRefinementsContainer}
                    sx={{
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {display: 'none'},
                    }}
                >
                    {displayPreferredFilters.map((filter) => {
                        const buttonStyleProps = getFilterButtonStyleProps(filter)
                        const iconColor = getFilterIconColor(filter)

                        return (
                            <Flex key={filter.key} {...styles?.buttonContainer}>
                                <Button
                                    variant="filter-button"
                                    onClick={filter.onClick}
                                    // Disabled when hitCount is 0 — the filter
                                    // is still rendered but not interactive
                                    isDisabled={filter.hitCount === 0}
                                    aria-label={filter.ariaLabel}
                                    size="medium"
                                    {...buttonStyleProps}
                                    width="auto"
                                    leftIcon={
                                        filter.iconId ? (
                                            <DynamicIcon
                                                iconId={filter.iconId}
                                                color={iconColor}
                                                boxSize={remCalc(16)}
                                                fontSize={remCalc(16)}
                                            />
                                        ) : null
                                    }
                                    rightIcon={
                                        // Live hit count — Dont show 0 when
                                        // filter has no matches in the current
                                        // result set
                                        filter.hitCount > 0 ? (
                                            <Text
                                                textStyle="body-xsmall"
                                                fontWeight="medium"
                                                color={getRightIconColor(filter)}
                                            >
                                                {filter.hitCount}
                                            </Text>
                                        ) : (
                                            <></>
                                        )
                                    }
                                >
                                    <Text {...styles?.filterText} color={buttonStyleProps.color}>
                                        {filter.label}
                                    </Text>
                                </Button>
                            </Flex>
                        )
                    })}
                </Box>
            )}
        </>
    )
}

RefinementsTab.propTypes = {
    filters: PropTypes.array,
    selectedFilterValues: PropTypes.object,
    toggleFilter: PropTypes.func,
    handleReset: PropTypes.func,
}

export default RefinementsTab
